import type { TastingPreferences } from '@recipes/contracts';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@recipes/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFeature, useHouseholds, useProfile } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

const NONE = '__none__';

/**
 * Tasting-note settings on the profile page. Saved immediately, independent of the big
 * profile form: location use, and the one fixed household visits are shared with.
 */
export function TastingPreferencesSection() {
  const { t } = useTranslation();
  const enabled = useFeature('tasting_notes');
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const { data: households } = useHouseholds();

  const save = useMutation({
    mutationFn: (patch: Partial<TastingPreferences>) =>
      api.users.updateProfile({ tastingPreferences: patch }),
    onSuccess: (next) => queryClient.setQueryData(queryKeys.profile, next),
    onError: (e) => toastError(e),
  });

  if (!enabled || !profile) return null;
  const prefs = profile.tastingPreferences;
  const list = households ?? [];
  // With exactly one household that one is used even when nothing is selected.
  const effectiveHousehold = prefs.defaultHouseholdId ?? (list.length === 1 ? list[0]!.id : null);

  return (
    <section
      className="mt-8 space-y-4 border-t pt-6"
      aria-labelledby="tasting-prefs-heading"
      data-testid="tasting-preferences"
    >
      <h2 id="tasting-prefs-heading" className="text-lg font-semibold">
        {t('tasting.prefs.title')}
      </h2>

      <div className="flex items-start gap-3">
        <Switch
          id="tasting-use-location"
          checked={prefs.useLocation}
          disabled={save.isPending}
          onCheckedChange={(useLocation) => save.mutate({ useLocation })}
        />
        <div>
          <Label htmlFor="tasting-use-location">{t('tasting.prefs.useLocation')}</Label>
          <p className="text-xs text-muted-foreground">{t('tasting.prefs.useLocationHint')}</p>
        </div>
      </div>

      {list.length > 0 && (
        <>
          <div>
            <Label htmlFor="tasting-default-household">{t('tasting.prefs.defaultHousehold')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('tasting.prefs.defaultHouseholdHint')}
            </p>
            <Select
              value={prefs.defaultHouseholdId ?? NONE}
              onValueChange={(v) => save.mutate({ defaultHouseholdId: v === NONE ? null : v })}
              disabled={save.isPending}
            >
              <SelectTrigger id="tasting-default-household" className="mt-1 max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>
                  {list.length === 1
                    ? t('tasting.prefs.onlyHousehold', { name: list[0]!.name })
                    : t('tasting.prefs.noHousehold')}
                </SelectItem>
                {list.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-3">
            <Switch
              id="tasting-share-default"
              checked={prefs.shareByDefault}
              disabled={save.isPending || !effectiveHousehold}
              onCheckedChange={(shareByDefault) => save.mutate({ shareByDefault })}
            />
            <div>
              <Label htmlFor="tasting-share-default">{t('tasting.prefs.shareByDefault')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('tasting.prefs.shareByDefaultHint')}
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
